/**
 * What a Kono super user sees after clicking the shield beside a price: where
 * the price came from, and the item's fields as editable inputs.
 *
 * The price story is assembled from whichever provenance the pricing pipeline
 * left behind — a vendor number with a markdown, a figure averaged from
 * comparable stock, a web-search finding with its citations, or an operator's
 * own entry. Only one of those applies to any item, so the panel shows the one
 * that does rather than a row of empty fields.
 *
 * A price can be typed in dollars or nudged by a percentage; both write the same
 * absolute price, since a percentage of a number that later changes would mean
 * something different by the time it was applied. Saving goes through the same
 * audited path as the internal Manage Inventory screen, so the change re-indexes
 * buyer search and survives the next scrape.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PriceComparableRef,
  PriceMoney,
  ProductEdit,
  ProductInsight,
  ProvenanceEvent,
  RecordProvenance,
  FxRateSuggestion,
  SuperUserApiError,
  fetchFxRate,
  fetchProductInsight,
  saveProductEdit,
} from "./superUserApi";
import { useSuperUserMode } from "../../buyerStateDefs";

type Props = {
  productId: string;
  onClose: () => void;
  /** Called after a successful save so the page can re-read the product. */
  onSaved: () => void;
};

/** Editable copies of the fields, as strings so inputs stay controlled. */
type Draft = {
  priceValue: string;
  /** ISO currency code the price is stored in; changed by Convert to USD. */
  priceCurrency: string;
  quantityValue: string;
  summary: string;
  standard: string;
  shape: string;
  condition: string;
  origin: string;
  dbMaterials: string;
  dimensions: DimensionDraft[];
  additionalSpecifications: string;
  sourcingNote: string;
  leadMin: string;
  leadMax: string;
  hiddenFromBuyerUI: boolean;
  note: string;
};

/**
 * One editable size. `to` is the upper bound of a vendor-quoted tolerance band
 * and is usually blank: most items are a single size.
 */
export type DimensionDraft = {
  name: string;
  value: string;
  to: string;
  unit: string;
};

const DEFAULT_DIMENSION_UNIT = "in";

const asText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

export const dimensionDraftsFrom = (
  dims: ProductInsight["fields"]["dimensions"],
): DimensionDraft[] => {
  if (!dims || typeof dims !== "object") return [];
  return Object.entries(dims)
    .map(([name, comp]) => {
      const lo = comp?.lo?.value;
      const hi = comp?.hi?.value;
      const unit = comp?.lo?.unit || comp?.hi?.unit || DEFAULT_DIMENSION_UNIT;
      return {
        name,
        value: lo != null ? String(lo) : hi != null ? String(hi) : "",
        to: hi != null && lo != null && hi !== lo ? String(hi) : "",
        unit,
      };
    })
    .filter((draft) => draft.value !== "");
};

/** The wire form the edit endpoint validates: name -> lo/hi measurement. */
export const dimensionsFromDrafts = (
  drafts: DimensionDraft[],
): NonNullable<ProductEdit["dimensions"]> => {
  const out: NonNullable<ProductEdit["dimensions"]> = {};
  for (const draft of drafts) {
    const name = draft.name.trim();
    if (!name || draft.value.trim() === "") continue;
    const lo = Number(draft.value);
    const hi = draft.to.trim() === "" ? lo : Number(draft.to);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) continue;
    out[name] = {
      lo: { value: lo, unit: draft.unit },
      hi: { value: hi, unit: draft.unit },
    };
  }
  return out;
};

const draftFrom = (insight: ProductInsight): Draft => ({
  priceValue: asText(insight.price.current?.value),
  priceCurrency: (insight.price.current?.currency || "USD").toUpperCase(),
  quantityValue: asText(insight.fields.quantity?.value),
  summary: asText(insight.fields.summary),
  standard: asText(insight.fields.standard),
  shape: asText(insight.fields.shape),
  condition: asText(insight.fields.condition),
  origin: asText(insight.fields.origin),
  dbMaterials: (insight.fields.dbMaterials || []).join(", "),
  dimensions: dimensionDraftsFrom(insight.fields.dimensions),
  additionalSpecifications: asText(insight.fields.additionalSpecifications),
  sourcingNote: asText(insight.fields.sourcingNote),
  leadMin: asText(insight.fields.estimatedLeadTimeDaysMin),
  leadMax: asText(insight.fields.estimatedLeadTimeDaysMax),
  hiddenFromBuyerUI: insight.fields.hiddenFromBuyerUI,
  note: "",
});

/**
 * Only what the operator actually changed, so an untouched field is never sent
 * as an edit — the audit trail is read as a record of intent, and a no-op diff
 * would pollute it.
 */
export const changedFields = (
  insight: ProductInsight,
  draft: Draft,
): ProductEdit => {
  const edit: ProductEdit = {};
  const base = draftFrom(insight);

  const price = Number(draft.priceValue);
  const priceChanged = draft.priceValue.trim() !== base.priceValue;
  const currencyChanged = draft.priceCurrency !== base.priceCurrency;
  if ((priceChanged || currencyChanged) && Number.isFinite(price)) {
    edit.price = {
      value: price,
      currency: draft.priceCurrency || insight.price.current?.currency || "USD",
      unit: insight.price.current?.unit,
    };
  }
  const quantity = Number(draft.quantityValue);
  if (
    draft.quantityValue.trim() !== base.quantityValue &&
    Number.isFinite(quantity)
  ) {
    edit.quantity = { value: quantity, unit: insight.fields.quantity?.unit };
  }
  for (const field of [
    "summary",
    "standard",
    "shape",
    "condition",
    "origin",
    "additionalSpecifications",
    "sourcingNote",
  ] as const) {
    if (draft[field] !== base[field]) edit[field] = draft[field];
  }
  if (draft.dbMaterials !== base.dbMaterials) {
    edit.dbMaterials = draft.dbMaterials
      .split(",")
      .map((m) => m.trim())
      .filter(Boolean);
  }
  // Sizes travel as a whole set: the server validates them against each other
  // and rebuilds the standardized copy and the title from the new numbers.
  const dims = dimensionsFromDrafts(draft.dimensions);
  if (
    JSON.stringify(dims) !==
    JSON.stringify(dimensionsFromDrafts(base.dimensions))
  ) {
    edit.dimensions = dims;
  }
  // Sent together: the backend validates the range as a pair, so changing one
  // bound alone would be checked against a value it never saw.
  const leadChanged =
    draft.leadMin !== base.leadMin || draft.leadMax !== base.leadMax;
  if (leadChanged) {
    const min = Number(draft.leadMin);
    const max = Number(draft.leadMax);
    if (Number.isFinite(min)) edit.estimatedLeadTimeDaysMin = min;
    if (Number.isFinite(max)) edit.estimatedLeadTimeDaysMax = max;
  }
  if (draft.hiddenFromBuyerUI !== base.hiddenFromBuyerUI) {
    edit.hiddenFromBuyerUI = draft.hiddenFromBuyerUI;
  }
  if (draft.note.trim()) edit.note = draft.note.trim();
  return edit;
};

/**
 * One piece's weight in the record's price unit (lb or kg), so a per-piece
 * price can be turned into the stored per-weight price and back. Null when
 * the price is not per weight or the piece weight is unknown.
 */
const pieceWeightInPriceUnit = (insight: ProductInsight): number | null => {
  const unit = (insight.price.current?.unit || "").toLowerCase();
  const w = insight.pieceWeight;
  if (!w) return null;
  if (unit === "lb" || unit === "lbs") return w.lb;
  if (unit === "kg" || unit === "kgs") return w.kg;
  return null;
};

const KG_PER_LB = 0.45359237;

/**
 * The vendor's listed quantity expressed in the record's price unit, when
 * both are weights (lb or kg). This is what the order form charges for the
 * whole lot, so pricing "per piece" against it makes the two screens agree
 * for single-piece records. Null otherwise.
 */
const listedWeightInPriceUnit = (
  insight: ProductInsight,
  quantityText: string,
): number | null => {
  const asWeight = (u: string): "lb" | "kg" | null => {
    const x = u.toLowerCase();
    if (x === "lb" || x === "lbs") return "lb";
    if (x === "kg" || x === "kgs") return "kg";
    return null;
  };
  const priceUnit = asWeight(insight.price.current?.unit || "");
  const qtyUnit = asWeight(insight.fields.quantity?.unit || "");
  const qty = Number(quantityText);
  if (!priceUnit || !qtyUnit || !(Number.isFinite(qty) && qty > 0)) return null;
  if (priceUnit === qtyUnit) return qty;
  return priceUnit === "lb" ? qty / KG_PER_LB : qty * KG_PER_LB;
};

/** Which weight a per-piece price is divided by. */
export type PieceBasis = "geometry" | "listed";

/**
 * The basis the panel opens with. A record whose listed quantity is the
 * weight of one piece (within 15% of the theoretical weight, or the only
 * weight known) is a single piece sold whole: the order form charges the
 * listed weight, so the per-piece price is read against it and the two
 * screens agree. A lot of several pieces keeps the theoretical one-piece
 * weight; the operator can switch either way.
 */
export const defaultPieceBasis = (
  geometryWeight: number | null,
  listedWeight: number | null,
): PieceBasis => {
  if (!listedWeight) return "geometry";
  if (!geometryWeight) return "listed";
  return Math.abs(listedWeight / geometryWeight - 1) <= 0.15 ? "listed" : "geometry";
};

const money2 = (n: number): string =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A price moved by a percentage, rounded to cents. */
export const adjustByPercent = (current: number, percent: number): number =>
  Math.round(current * (1 + percent / 100) * 100) / 100;

const money = (value?: number, unit?: string, currency = "USD"): string =>
  value === undefined || value === null
    ? "—"
    : `${currency} ${value.toFixed(2)}${unit ? `/${unit}` : ""}`;

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="grid grid-cols-3 gap-3 py-1.5">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className="col-span-2 text-xs text-gray-800">{children}</div>
  </div>
);

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    {children}
  </label>
);

const inputClass =
  "mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm";

/**
 * Convert a listing priced in another currency to USD (Kono super user).
 *
 * The exchange rate is a suggestion from a named source with its date; the
 * operator may type another one. Pressing the button only fills the price and
 * currency in the form (and a note for the record): nothing is stored until
 * Save, which goes through the ordinary audited edit. The vendor's original
 * price and currency stay on the record as `vendorOriginal`.
 */
export const fxConvertedPrice = (value: number, rate: number): string =>
  (Math.round(value * rate * 10000) / 10000).toString();

const FxConvertBlock = ({
  fromCurrency,
  value,
  unit,
  onApply,
}: {
  fromCurrency: string;
  value: number;
  unit?: string;
  onApply: (converted: string, note: string) => void;
}) => {
  const [suggestion, setSuggestion] = useState<FxRateSuggestion | null>(null);
  const [rateText, setRateText] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "unavailable">("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    fetchFxRate(fromCurrency, "USD")
      .then((r) => {
        if (cancelled) return;
        setSuggestion(r);
        setRateText(String(r.rate));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [fromCurrency]);

  const rate = Number(rateText);
  const usable = Number.isFinite(rate) && rate > 0 && Number.isFinite(value);
  const preview = usable ? fxConvertedPrice(value, rate) : "";
  const basis = suggestion
    ? `${suggestion.source}, ${suggestion.asOf}${suggestion.stale ? " (last stored rate; live source unreachable)" : ""}`
    : "rate typed by hand";
  const overridden = suggestion && Number(rateText) !== suggestion.rate;

  return (
    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-gray-700 sm:col-span-2">
      <div className="font-medium text-amber-900">Convert to USD</div>
      <p className="mt-0.5">
        This listing is priced in {fromCurrency}. The vendor's original price stays on the record; the
        converted price is what buyers see.
      </p>
      <div className="mt-1.5 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="text-[0.65rem] font-medium text-gray-500">1 {fromCurrency} = USD</span>
          <input
            className="mt-0.5 w-28 rounded border border-gray-300 px-2 py-1 text-sm"
            inputMode="decimal"
            value={rateText}
            onChange={(e) => setRateText(e.target.value)}
            placeholder={status === "loading" ? "loading..." : "e.g. 0.72"}
          />
        </label>
        <span className="pb-1.5">
          {usable
            ? `${fromCurrency} ${value} -> USD ${preview}${unit ? ` per ${unit}` : ""}`
            : "enter a rate"}
        </span>
        <button
          type="button"
          disabled={!usable}
          onClick={() =>
            onApply(
              preview,
              `Converted from ${fromCurrency} ${value}${unit ? `/${unit}` : ""} to USD at ${rate}` +
                ` (${overridden ? `operator rate; suggested ${suggestion?.rate} from ${basis}` : basis})`,
            )
          }
          className="rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          Use this rate
        </button>
      </div>
      <p className="mt-1 text-[0.65rem] text-gray-500">
        {status === "loading"
          ? "Fetching today's reference rate..."
          : status === "unavailable"
            ? "No reference rate available right now; type the rate you want to use."
            : `Suggested: ${suggestion?.rate} (${basis}).${overridden ? " You changed it; the note will say so." : ""}`}
      </p>
    </div>
  );
};

const DimensionRows = ({
  drafts,
  vocabulary,
  onChange,
}: {
  drafts: DimensionDraft[];
  vocabulary?: ProductInsight["dimensionVocabulary"];
  onChange: (next: DimensionDraft[]) => void;
}) => {
  const shapeNames = vocabulary?.shapeNames || [];
  // The shape's own sizes first, then everything else for the odd item that
  // carries another.
  const nameOptions = [
    ...shapeNames,
    ...(vocabulary?.allNames || []).filter((n) => !shapeNames.includes(n)),
  ];
  const unitsFor = (name: string) =>
    (/weight/i.test(name)
      ? vocabulary?.weightUnits
      : vocabulary?.distanceUnits) || [DEFAULT_DIMENSION_UNIT];
  const update = (index: number, patch: Partial<DimensionDraft>) =>
    onChange(drafts.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  const used = drafts.map((d) => d.name);
  const unused = nameOptions.filter((n) => !used.includes(n));
  const cell = "rounded border border-gray-300 px-2 py-1.5 text-sm";

  return (
    <div>
      <span className="text-xs font-medium text-gray-500">
        Dimensions (what buyers match on; the title is updated to match)
      </span>
      {drafts.length === 0 && (
        <p className="mt-1 text-xs text-gray-500">
          No dimensions recorded — buyers cannot match this item on size.
        </p>
      )}
      {drafts.map((draft, index) => {
        const units = unitsFor(draft.name);
        return (
          <div key={index} className="mt-1 flex items-center gap-2">
            <select
              className={`${cell} flex-1`}
              value={draft.name}
              onChange={(e) => update(index, { name: e.target.value })}
            >
              {!nameOptions.includes(draft.name) && (
                <option value={draft.name}>{draft.name}</option>
              )}
              {nameOptions.map((name) => (
                <option key={name} value={name}>
                  {name.replace(/_/g, " ")}
                </option>
              ))}
            </select>
            <input
              className={`${cell} w-24`}
              inputMode="decimal"
              value={draft.value}
              onChange={(e) => update(index, { value: e.target.value })}
              placeholder="value"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              className={`${cell} w-24`}
              inputMode="decimal"
              value={draft.to}
              onChange={(e) => update(index, { to: e.target.value })}
              placeholder="(optional)"
              title="Upper bound of a vendor-quoted tolerance band. Leave blank for an exact size."
            />
            <select
              className={`${cell} w-20`}
              value={draft.unit}
              onChange={(e) => update(index, { unit: e.target.value })}
            >
              {!units.includes(draft.unit) && (
                <option value={draft.unit}>{draft.unit}</option>
              )}
              {units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
              onClick={() => onChange(drafts.filter((_, i) => i !== index))}
              title="Remove this dimension"
            >
              Remove
            </button>
          </div>
        );
      })}
      {unused.length > 0 && (
        <button
          type="button"
          className="mt-2 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          onClick={() =>
            onChange([
              ...drafts,
              {
                name: unused[0],
                value: "",
                to: "",
                unit: unitsFor(unused[0])[0],
              },
            ])
          }
        >
          + Add dimension
        </button>
      )}
    </div>
  );
};

const shortDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : "date unknown";

const comparableLine = (ref: PriceComparableRef): string =>
  `USD ${ref.perLbUsd.toFixed(2)}/lb — ${
    ref.supplierName || ref.supplierId || "unknown vendor"
  }, ingested ${shortDate(ref.seenAt)}`;

/**
 * The cheapest and dearest real price that went into a comparable-based
 * estimate, each with its vendor and ingestion date, so a single off-market
 * vendor pulling the average can be spotted. For an ML-priced item, shows the
 * comparables the rule-based method would have used — the models train on the
 * whole history, so there is no short list to show for them.
 */
const ComparableRange = ({
  estimate,
  ruleBased,
}: {
  estimate: Record<string, any>;
  ruleBased: { price: PriceMoney; priceEstimate: Record<string, any> } | null;
}) => {
  const source =
    estimate.basisLow || estimate.basisHigh
      ? estimate
      : ruleBased?.priceEstimate || null;
  if (!source) return null;
  const low = source.basisLow as PriceComparableRef | undefined;
  const high = source.basisHigh as PriceComparableRef | undefined;
  if (!low && !high) return null;
  const fromRuleBased = source !== estimate;
  const spread = source.basisSpreadPct;
  const superseded = Array.isArray(source.supersededItemIds)
    ? source.supersededItemIds.length
    : 0;
  return (
    <>
      {fromRuleBased && ruleBased && (
        <Row label="Rule-based alternative">
          {money(ruleBased.price.value, ruleBased.price.unit)} from{" "}
          {ruleBased.priceEstimate.sampleSize} real comparable price
          {ruleBased.priceEstimate.sampleSize === 1 ? "" : "s"} (not the served
          price)
        </Row>
      )}
      {low && <Row label="Lowest price used">{comparableLine(low)}</Row>}
      {high && high.id !== low?.id && (
        <Row label="Highest price used">{comparableLine(high)}</Row>
      )}
      {typeof spread === "number" && high && high.id !== low?.id && (
        <Row label="Spread">
          <span className={spread > 20 ? "text-amber-700" : ""}>
            {spread.toFixed(1)}% between lowest and highest
            {low?.supplierId &&
            high?.supplierId &&
            low.supplierId !== high.supplierId
              ? " (different vendors — see their score cards in Catalogs → Vendors)"
              : ""}
          </span>
        </Row>
      )}
      {superseded > 0 && (
        <Row label="Set aside">
          {superseded} older same-vendor price{superseded === 1 ? "" : "s"}{" "}
          superseded by a newer one
        </Row>
      )}
    </>
  );
};

const SIZE_CLASS_LABEL: Record<string, string> = {
  large: "large distributors",
  medium: "medium vendors",
  small: "small vendors",
  all: "all vendors (pooled)",
};

const KIND_STYLE: Record<ProvenanceEvent["kind"], string> = {
  vendor: "bg-green-100 text-green-800",
  internal: "bg-gray-200 text-gray-700",
  edit: "bg-blue-100 text-blue-800",
};
const KIND_LABEL: Record<ProvenanceEvent["kind"], string> = {
  vendor: "vendor",
  internal: "internal",
  edit: "edit",
};

/**
 * The record's story: where it first came from, what has touched it since,
 * and why buyers can still see it — an internal republish restarts the
 * visibility clock like a vendor sighting does, and this is where that shows.
 */
export const ProvenanceSection = ({
  provenance,
}: {
  provenance: RecordProvenance;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 rounded-md border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-[#00062e]">
        Where this record came from
      </h3>
      <ul className="mt-2 space-y-1 text-xs text-gray-800">
        {provenance.summary.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      {provenance.events.length > 0 && (
        <button
          type="button"
          className="mt-2 text-xs text-blue-700 underline"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide" : "Show"} timeline ({provenance.events.length}
          {provenance.omittedUploads > 0
            ? ` shown, ${provenance.omittedUploads} more omitted`
            : ""}
          )
        </button>
      )}
      {open && (
        <ol className="mt-2 space-y-1">
          {provenance.events.map((event, i) => (
            <li key={`${event.at}-${i}`} className="text-xs text-gray-700">
              <span className="text-gray-500">
                {new Date(event.at).toLocaleString()}
              </span>{" "}
              <span
                className={`rounded px-1 py-0.5 text-[10px] ${
                  KIND_STYLE[event.kind]
                }`}
              >
                {KIND_LABEL[event.kind]}
              </span>{" "}
              {event.label}
              {event.detail ? (
                <span className="text-gray-500"> — {event.detail}</span>
              ) : null}
              {event.uploadId ? (
                <span className="text-gray-400">
                  {" "}
                  · upload {event.uploadId}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

/**
 * The price story, read-only: vendor, price now and where it came from, when
 * and how the data was obtained, the vendor's own number, how an estimate was
 * built and what it was based on, web-search provenance, pending suggestions.
 * Shown in the super-user panel and, per item, in the quote sourcing view.
 */
export const PriceStory = ({
  insight,
  className = "",
}: {
  insight: ProductInsight;
  className?: string;
}) => {
  const comparison = insight.price.vendorComparison;
  const provenance = insight.price.provenance;
  const estimate = insight.price.estimate;
  const suggestion = insight.price.suggestion;
  return (
    <div className={`rounded-md bg-gray-50 p-4 ${className}`}>
      <Row label="Vendor">
        {insight.vendor.name || "Unknown"}
        {insight.vendor.sourceProfile
          ? ` (feed: ${insight.vendor.sourceProfile})`
          : ""}
      </Row>
      <Row label="Price now">
        {money(
          insight.price.current?.value,
          insight.price.current?.unit,
          insight.price.current?.currency,
        )}
        {(() => {
          const v = insight.price.current?.value;
          if (typeof v !== "number") return null;
          const cur = insight.price.current?.currency || "USD";
          const unit = insight.price.current?.unit;
          const geo = pieceWeightInPriceUnit(insight);
          const listed = listedWeightInPriceUnit(
            insight,
            String(insight.fields.quantity?.value ?? ""),
          );
          const parts = [
            geo && `${cur} ${money2(v * geo)} per piece by theoretical weight (${money2(geo)} ${unit})`,
            listed && `${cur} ${money2(v * listed)} for the listed ${money2(listed)} ${unit}`,
          ].filter(Boolean);
          return parts.length ? (
            <span className="text-gray-500"> (≈ {parts.join("; ")})</span>
          ) : null;
        })()}
      </Row>
      <Row label="Where it came from">{insight.price.origin}</Row>
      {insight.dataOrigin && (
        <>
          <Row label="Data obtained">
            {insight.dataOrigin.obtainedAt
              ? new Date(insight.dataOrigin.obtainedAt).toLocaleString()
              : "Unknown"}
          </Row>
          <Row label="How we got it">
            {insight.dataOrigin.method}
            {insight.dataOrigin.methodCode
              ? ` (${insight.dataOrigin.methodCode})`
              : ""}
          </Row>
          {insight.dataOrigin.vendorConfirmedAt && (
            <Row label="Vendor last confirmed">
              {new Date(insight.dataOrigin.vendorConfirmedAt).toLocaleString()}
            </Row>
          )}
          {insight.dataOrigin.sourceUrl && (
            <Row label="Source URL">
              <a
                href={insight.dataOrigin.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-blue-700 underline"
              >
                {insight.dataOrigin.sourceUrl}
              </a>
            </Row>
          )}
        </>
      )}

      {comparison && (
        <>
          <Row label="Vendor's own price">
            {money(comparison.vendorValue, comparison.vendorUnit)}
          </Row>
          <Row label="Difference">
            {comparison.comparable ? (
              <>
                {comparison.differenceValue >= 0 ? "+" : ""}
                {comparison.differenceValue.toFixed(2)} (
                {comparison.differencePct >= 0 ? "+" : ""}
                {comparison.differencePct.toFixed(1)}%){" "}
                {comparison.differencePct < 0 ? "below vendor" : "above vendor"}
              </>
            ) : (
              // Comparing a per-foot price with a per-pound one would
              // read as a discount that does not exist.
              <span className="text-gray-500">
                Not comparable: vendor quoted per {comparison.vendorUnit}, we
                sell per {insight.price.current?.unit}
              </span>
            )}
          </Row>
        </>
      )}

      {estimate && (
        <>
          <Row label="How it was estimated">{estimate.formula}</Row>
          <Row label="Based on">
            {estimate.tier === "ml"
              ? `${estimate.sampleSize} model${
                  estimate.sampleSize === 1 ? "" : "s"
                } trained on real vendor prices`
              : `${estimate.sampleSize} comparable item${
                  estimate.sampleSize === 1 ? "" : "s"
                }`}
            {estimate.materialFamily ? ` of ${estimate.materialFamily}` : ""}
          </Row>
          {estimate.tier === "ml" && estimate.modelSizeClass && (
            <Row label="Vendor size model">
              {estimate.sizeClassNote ? (
                <span className="text-amber-700">{estimate.sizeClassNote}</span>
              ) : (
                `Models trained for ${
                  SIZE_CLASS_LABEL[String(estimate.modelSizeClass)] ||
                  estimate.modelSizeClass
                }, the vendor's own class`
              )}
            </Row>
          )}
          <ComparableRange
            estimate={estimate}
            ruleBased={insight.price.estimates?.ruleBased || null}
          />
          {estimate.tier !== "ml" && insight.price.estimates?.ml && (
            <Row label="ML alternative">
              {money(
                insight.price.estimates.ml.price.value,
                insight.price.estimates.ml.price.unit,
              )}{" "}
              {insight.price.estimates.identicalOverride
                ? "(not served: a real price for an identical item takes precedence)"
                : insight.price.estimates.mlGated
                  ? "(not served: the models disagreed beyond the gate)"
                  : "(not served)"}
            </Row>
          )}
        </>
      )}

      {provenance && (
        <>
          <Row label="Source">
            {provenance.siteName || provenance.source || provenance.method}
            {typeof provenance.confidence === "number"
              ? ` (confidence ${provenance.confidence})`
              : ""}
          </Row>
          {provenance.formula && (
            <Row label="Working">{provenance.formula}</Row>
          )}
          {Array.isArray(provenance.citations) &&
            provenance.citations.length > 0 && (
              <Row label="Cited">
                <ul className="space-y-0.5">
                  {provenance.citations.map((c: any, i: number) => (
                    <li key={i}>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 underline"
                      >
                        {c.title || c.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </Row>
            )}
          {provenance.acceptedByEmail && (
            <Row label="Accepted by">{provenance.acceptedByEmail}</Row>
          )}
        </>
      )}

      {suggestion && (
        <Row label="Pending suggestion">
          {money(
            suggestion.suggestedPrice?.value,
            suggestion.suggestedPrice?.unit,
          )}{" "}
          — {suggestion.formula || suggestion.method} (not applied)
        </Row>
      )}
    </div>
  );
};

const dimensionText = (dims: ProductInsight["fields"]["dimensions"]): string =>
  dimensionDraftsFrom(dims)
    .map(
      (d) =>
        `${d.name.replace(/_/g, " ")} ${d.value}${d.to ? `–${d.to}` : ""} ${d.unit}`,
    )
    .join(" × ");

/** The item's fields as the super-user panel edits them, read-only. */
export const ItemFieldsSummary = ({
  fields,
  className = "",
}: {
  fields: ProductInsight["fields"];
  className?: string;
}) => {
  const lead =
    fields.estimatedLeadTimeDaysMin != null ||
    fields.estimatedLeadTimeDaysMax != null
      ? `${fields.estimatedLeadTimeDaysMin ?? "?"}–${fields.estimatedLeadTimeDaysMax ?? "?"} days`
      : "—";
  const dims = dimensionText(fields.dimensions);
  return (
    <div className={`rounded-md border border-gray-200 p-4 ${className}`}>
      <Row label="Material grades">
        {(fields.dbMaterials || []).join(", ") || "—"}
      </Row>
      <Row label="Standard">{fields.standard || "—"}</Row>
      <Row label="Shape">{fields.shape || "—"}</Row>
      <Row label="Condition">{fields.condition || "—"}</Row>
      <Row label="Origin / compliance">{fields.origin || "—"}</Row>
      <Row label="Dimensions">{dims || "—"}</Row>
      <Row label="Quantity">
        {fields.quantity?.value != null
          ? `${fields.quantity.value} ${fields.quantity.unit || ""}`.trim()
          : "—"}
      </Row>
      <Row label="Lead time">{lead}</Row>
      {fields.additionalSpecifications && (
        <Row label="Additional specifications">
          {fields.additionalSpecifications}
        </Row>
      )}
      {fields.sourcingNote && (
        <Row label="Sourcing note">{fields.sourcingNote}</Row>
      )}
      {fields.hiddenFromBuyerUI && (
        <Row label="Visibility">
          <span className="text-amber-700">Hidden from buyers</span>
        </Row>
      )}
    </div>
  );
};

/** Who changed what on this item, most recent first. */
export const RecentChanges = ({
  audit,
  className = "",
}: {
  audit: ProductInsight["audit"];
  className?: string;
}) =>
  audit.length === 0 ? null : (
    <div className={className}>
      <h3 className="text-sm font-semibold text-[#00062e]">Recent changes</h3>
      <ul className="mt-2 space-y-2">
        {audit.map((entry) => (
          <li key={entry.id} className="text-xs text-gray-600">
            <div className="text-gray-500">
              {new Date(entry.ts).toLocaleString()} ·{" "}
              {entry.editorEmail || "unknown"}
              {entry.note ? ` · ${entry.note}` : ""}
            </div>
            <ul className="ml-4 list-disc">
              {entry.changes.map((change, i) => (
                <li key={i}>
                  {change.field}: {JSON.stringify(change.from)} →{" "}
                  {JSON.stringify(change.to)}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );

export const SuperUserPricePanel = ({ productId, onClose, onSaved }: Props) => {
  const deactivate = useSuperUserMode((state) => state.deactivate);
  const [insight, setInsight] = useState<ProductInsight | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [percent, setPercent] = useState("");
  // What the operator is typing in the per-piece box; null shows the value
  // derived from the per-weight price instead.
  const [perPieceText, setPerPieceText] = useState<string | null>(null);
  const [pieceBasis, setPieceBasis] = useState<PieceBasis>("geometry");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // A lapsed re-authentication closes the tools rather than showing an error the
  // operator can do nothing about; the corner button re-opens them.
  const handleError = useCallback(
    (e: unknown, fallback: string) => {
      console.error(fallback, e);
      if (e instanceof SuperUserApiError && e.reauthRequired) {
        deactivate();
        onClose();
        return;
      }
      setError((e as any)?.message || fallback);
    },
    [deactivate, onClose],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchProductInsight(productId);
      setInsight(data);
      const fresh = draftFrom(data);
      setDraft(fresh);
      setPerPieceText(null);
      setPieceBasis(
        defaultPieceBasis(
          pieceWeightInPriceUnit(data),
          listedWeightInPriceUnit(data, fresh.quantityValue),
        ),
      );
    } catch (e) {
      handleError(e, "Could not load this item's pricing detail.");
    } finally {
      setLoading(false);
    }
  }, [productId, handleError]);

  useEffect(() => {
    load();
  }, [load]);

  const edit = useMemo(
    () => (insight && draft ? changedFields(insight, draft) : {}),
    [insight, draft],
  );
  const editedFieldCount = Object.keys(edit).filter((k) => k !== "note").length;

  const save = async () => {
    if (!insight || editedFieldCount === 0) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await saveProductEdit(insight.productId, edit);
      setMessage(
        `Saved ${result.changed} change${result.changed === 1 ? "" : "s"}.`,
      );
      setPercent("");
      onSaved();
      await load();
    } catch (e) {
      handleError(e, "Could not save the changes.");
    } finally {
      setSaving(false);
    }
  };

  const applyPercent = () => {
    const pct = Number(percent);
    const current = Number(draft?.priceValue);
    if (!draft || !Number.isFinite(pct) || !Number.isFinite(current)) return;
    setDraft({ ...draft, priceValue: String(adjustByPercent(current, pct)) });
    setPerPieceText(null);
  };

  const geometryWeight = insight ? pieceWeightInPriceUnit(insight) : null;
  const listedWeight =
    insight && draft ? listedWeightInPriceUnit(insight, draft.quantityValue) : null;
  // Fall back to whichever basis exists when the chosen one is unavailable.
  const basis: PieceBasis =
    pieceBasis === "geometry"
      ? geometryWeight
        ? "geometry"
        : "listed"
      : listedWeight
        ? "listed"
        : "geometry";
  const pieceWeight = basis === "geometry" ? geometryWeight : listedWeight;
  const perPieceDerived =
    pieceWeight && draft && Number.isFinite(Number(draft.priceValue))
      ? Number(draft.priceValue) * pieceWeight
      : null;
  /** Per-piece dollars typed → per-weight price stored (4 decimals keep the round trip within a cent). */
  const setPerPiece = (text: string) => {
    setPerPieceText(text);
    const perPiece = Number(text.replace(/[,$\s]/g, ""));
    if (!draft || !pieceWeight || !Number.isFinite(perPiece)) return;
    setDraft({
      ...draft,
      priceValue: (perPiece / pieceWeight).toFixed(4),
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-auto bg-black/50 p-4 py-10">
      <div className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#00062e]">
              Price &amp; item detail
            </h2>
            <p className="text-xs text-gray-500">
              Kono super user view. Changes are recorded against your account.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        {loading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}
        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}
        {message && (
          <div className="mt-4 text-sm text-green-700">{message}</div>
        )}

        {insight && draft && (
          <>
            <PriceStory insight={insight} className="mt-5" />

            {insight.provenance && (
              <ProvenanceSection provenance={insight.provenance} />
            )}

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field
                label={`Price${
                  insight.price.current?.unit
                    ? ` (${insight.price.current.currency || "USD"} per ${
                        insight.price.current.unit
                      })`
                    : ""
                }`}
              >
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={draft.priceValue}
                  onChange={(e) => {
                    setDraft({ ...draft, priceValue: e.target.value });
                    setPerPieceText(null);
                  }}
                />
                {draft.priceCurrency !== (insight.price.current?.currency || "USD").toUpperCase() && (
                  <span className="mt-1 block text-xs text-amber-800">
                    Will be stored in {draft.priceCurrency} on Save (was{" "}
                    {insight.price.current?.currency || "USD"}).
                  </span>
                )}
              </Field>
              {draft.priceCurrency !== "USD" &&
                typeof insight.price.current?.value === "number" && (
                  <FxConvertBlock
                    fromCurrency={draft.priceCurrency}
                    value={Number(draft.priceValue) || insight.price.current.value}
                    unit={insight.price.current?.unit}
                    onApply={(converted, note) => {
                      setDraft({
                        ...draft,
                        priceValue: converted,
                        priceCurrency: "USD",
                        note: draft.note.trim() ? `${draft.note.trim()} ${note}` : note,
                      });
                      setPerPieceText(null);
                    }}
                  />
                )}
              {pieceWeight && (
                <Field
                  label={`Or price per piece (${
                    insight.price.current?.currency || "USD"
                  }; one piece = ${money2(pieceWeight)} ${
                    insight.price.current?.unit
                  })`}
                >
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="e.g. 9376.94"
                    value={
                      perPieceText ??
                      (perPieceDerived !== null
                        ? perPieceDerived.toFixed(2)
                        : "")
                    }
                    onChange={(e) => setPerPiece(e.target.value)}
                    onBlur={() => setPerPieceText(null)}
                  />
                  <div className="mt-1 space-y-0.5 text-xs text-gray-600">
                    {(
                      [
                        {
                          id: "geometry" as PieceBasis,
                          weight: geometryWeight,
                          text: "theoretical weight from dimensions and density",
                        },
                        {
                          id: "listed" as PieceBasis,
                          weight: listedWeight,
                          text: "listed quantity, what the order form charges for the whole lot",
                        },
                      ] as const
                    ).map((o) =>
                      o.weight ? (
                        <label key={o.id} className="flex items-start gap-1.5">
                          <input
                            type="radio"
                            name="piece-basis"
                            className="mt-0.5"
                            checked={basis === o.id}
                            onChange={() => {
                              setPieceBasis(o.id);
                              // Keep the typed piece price; re-derive the per-weight price.
                              const typed = perPieceText ?? perPieceDerived?.toFixed(2);
                              const perPiece = Number((typed || "").replace(/[,$\s]/g, ""));
                              if (draft && Number.isFinite(perPiece) && typed) {
                                setPerPieceText(typed);
                                setDraft({
                                  ...draft,
                                  priceValue: (perPiece / o.weight!).toFixed(4),
                                });
                              }
                            }}
                          />
                          <span>
                            {money2(o.weight)} {insight.price.current?.unit}: {o.text}
                          </span>
                        </label>
                      ) : null,
                    )}
                    <p className="text-gray-500">
                      The record keeps the price per{" "}
                      {insight.price.current?.unit}; the piece price is divided
                      by the weight chosen above.
                    </p>
                  </div>
                </Field>
              )}
              <Field label="Or adjust by %">
                <div className="mt-1 flex gap-2">
                  <input
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    inputMode="decimal"
                    placeholder="e.g. -7.5"
                    value={percent}
                    onChange={(e) => setPercent(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={applyPercent}
                    disabled={!percent.trim()}
                    className="whitespace-nowrap rounded border-2 border-[#00062e] px-3 text-sm font-bold text-[#00062e] disabled:opacity-40"
                  >
                    Apply
                  </button>
                </div>
              </Field>

              <Field label="Material grades (comma separated)">
                <input
                  className={inputClass}
                  value={draft.dbMaterials}
                  onChange={(e) =>
                    setDraft({ ...draft, dbMaterials: e.target.value })
                  }
                />
              </Field>
              <Field label="Standard">
                <input
                  className={inputClass}
                  value={draft.standard}
                  onChange={(e) =>
                    setDraft({ ...draft, standard: e.target.value })
                  }
                />
              </Field>
              <Field label="Shape">
                <input
                  className={inputClass}
                  value={draft.shape}
                  onChange={(e) =>
                    setDraft({ ...draft, shape: e.target.value })
                  }
                />
              </Field>
              <Field label="Condition">
                <input
                  className={inputClass}
                  value={draft.condition}
                  onChange={(e) =>
                    setDraft({ ...draft, condition: e.target.value })
                  }
                />
              </Field>
              <Field label="Origin / compliance">
                <input
                  className={inputClass}
                  value={draft.origin}
                  onChange={(e) =>
                    setDraft({ ...draft, origin: e.target.value })
                  }
                />
              </Field>
              <Field
                label={`Quantity${
                  insight.fields.quantity?.unit
                    ? ` (${insight.fields.quantity.unit})`
                    : ""
                }`}
              >
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={draft.quantityValue}
                  onChange={(e) =>
                    setDraft({ ...draft, quantityValue: e.target.value })
                  }
                />
              </Field>
              <Field label="Lead time min (days)">
                <input
                  className={inputClass}
                  inputMode="numeric"
                  value={draft.leadMin}
                  onChange={(e) =>
                    setDraft({ ...draft, leadMin: e.target.value })
                  }
                />
              </Field>
              <Field label="Lead time max (days)">
                <input
                  className={inputClass}
                  inputMode="numeric"
                  value={draft.leadMax}
                  onChange={(e) =>
                    setDraft({ ...draft, leadMax: e.target.value })
                  }
                />
              </Field>
            </div>

            <div className="mt-4 space-y-4">
              <DimensionRows
                drafts={draft.dimensions}
                vocabulary={insight.dimensionVocabulary}
                onChange={(dimensions) => setDraft({ ...draft, dimensions })}
              />
              <Field label="Summary (buyer-facing title)">
                <input
                  className={inputClass}
                  value={draft.summary}
                  onChange={(e) =>
                    setDraft({ ...draft, summary: e.target.value })
                  }
                />
              </Field>
              <Field label="Additional specifications">
                <textarea
                  className={`${inputClass} h-20`}
                  value={draft.additionalSpecifications}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      additionalSpecifications: e.target.value,
                    })
                  }
                />
              </Field>
              <Field label="Sourcing note (shown to vendors and operators)">
                <textarea
                  className={`${inputClass} h-16`}
                  placeholder="e.g. 1 pc x 12 ft; cut in half to ship"
                  value={draft.sourcingNote}
                  onChange={(e) =>
                    setDraft({ ...draft, sourcingNote: e.target.value })
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Goes on vendor RFQ emails for this item, the quote PDF and the
                  order confirmation, and Manage Inventory. Not shown on the
                  buyer product page.
                </p>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.hiddenFromBuyerUI}
                  onChange={(e) =>
                    setDraft({ ...draft, hiddenFromBuyerUI: e.target.checked })
                  }
                />
                Hide this item from buyers
              </label>
              <Field label="Note for the record (optional)">
                <input
                  className={inputClass}
                  placeholder="Why this change?"
                  value={draft.note}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                />
              </Field>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={save}
                disabled={saving || editedFieldCount === 0}
                className="rounded-full bg-[#00062e] px-6 py-2 font-bold text-white disabled:opacity-40"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border-2 border-[#00062e] px-6 py-2 font-bold text-[#00062e]"
              >
                Close
              </button>
              <span className="text-xs text-gray-500">
                {editedFieldCount === 0
                  ? "No changes yet"
                  : `${editedFieldCount} field${
                      editedFieldCount === 1 ? "" : "s"
                    } changed`}
              </span>
            </div>

            <RecentChanges
              audit={insight.audit}
              className="mt-6 border-t border-gray-100 pt-4"
            />
          </>
        )}
      </div>
    </div>
  );
};
